(() => {
  const ELEMENT_NAME = 'lm-free-shipping-progress';

  if (customElements.get(ELEMENT_NAME)) return;

  /*
   * ÚNICA TABLA DE MONTOS PARA AMBOS CARRITOS.
   * Para modificar una meta regional, cambia solo "minimum" aquí.
   * Los valores están expresados en pesos chilenos, no en centavos.
   */
  const REGIONS = [
    { code: 'AP', name: 'Región de Arica y Parinacota', minimum: 79990 },
    { code: 'TA', name: 'Región de Tarapacá', minimum: 72990 },
    { code: 'AN', name: 'Región de Antofagasta', minimum: 69990 },
    { code: 'AT', name: 'Región de Atacama', minimum: 57990 },
    { code: 'CO', name: 'Región de Coquimbo', minimum: 49990 },
    { code: 'VA', name: 'Región de Valparaíso', minimum: 44990 },
    { code: 'RM', name: 'Región Metropolitana de Santiago', minimum: 34990 },
    { code: 'OH', name: 'Región del Libertador General Bernardo O’Higgins', minimum: 47990 },
    { code: 'MA', name: 'Región del Maule', minimum: 49990 },
    { code: 'NB', name: 'Región de Ñuble', minimum: 53990 },
    { code: 'BI', name: 'Región del Biobío', minimum: 55990 },
    { code: 'AR', name: 'Región de La Araucanía', minimum: 57990 },
    { code: 'LR', name: 'Región de Los Ríos', minimum: 59990 },
    { code: 'LL', name: 'Región de Los Lagos', minimum: 59990 },
    { code: 'AI', name: 'Región de Aysén', minimum: 94990 },
    { code: 'MG', name: 'Región de Magallanes y de la Antártica Chilena', minimum: 94990 },
  ];

  /*
   * PRODUCTOS EXCLUIDOS DE LA SUMATORIA.
   * El handle corresponde a la parte final de la URL del producto.
   * Todas las variantes del producto quedan excluidas automáticamente.
   */
  const EXCLUDED_PRODUCT_HANDLES = new Set([
    'the-tiny-paper-club',
  ]);

  const REGION_BY_CODE = new Map(REGIONS.map((region) => [region.code, region]));
  const STORAGE_KEY = 'lm_free_shipping_region_v1';
  const PRIVATE_CART_ATTRIBUTE = '__lm_region_envio_gratis';
  const REGION_CHANGE_EVENT = 'lm:free-shipping-region-change';
  const instances = new Set();

  let pendingCartRequest = null;
  let refreshTimer = null;
  let currentCartToken = '';
  let currentItemCount = 0;
  let lastMirroredKey = '';

  const moneyFormatter = new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  });

  function formatMoney(value) {
    const amount = Math.max(0, Math.round(Number(value) || 0));
    return moneyFormatter.format(amount);
  }

  function getCartUrl() {
    return `${window.routes?.cart_url || '/cart'}.js`;
  }

  function getCartUpdateUrl() {
    return `${window.routes?.cart_update_url || '/cart/update'}.js`;
  }

  function readStoredRegion() {
    try {
      const storedRegion = window.localStorage.getItem(STORAGE_KEY);

      if (storedRegion && REGION_BY_CODE.has(storedRegion)) {
        return storedRegion;
      }

      if (storedRegion) window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('No fue posible leer la región guardada.', error);
    }

    return '';
  }

  function storeRegion(regionCode) {
    try {
      if (regionCode) {
        window.localStorage.setItem(STORAGE_KEY, regionCode);
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.warn('No fue posible guardar la región.', error);
    }
  }

  function isExcludedCartItem(item) {
    const handle = String(item?.handle || '').trim().toLowerCase();
    return EXCLUDED_PRODUCT_HANDLES.has(handle);
  }

  function calculateEligibleTotals(cart) {
    return (cart?.items || []).reduce(
      (totals, item) => {
        const linePrice = Number.parseInt(
          item?.final_line_price ?? item?.line_price ?? 0,
          10
        ) || 0;

        if (isExcludedCartItem(item)) {
          totals.excluded += linePrice;
        } else {
          totals.eligible += linePrice;
        }

        return totals;
      },
      { eligible: 0, excluded: 0 }
    );
  }

  function fetchCart() {
    if (pendingCartRequest) return pendingCartRequest;

    pendingCartRequest = fetch(getCartUrl(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Shopify respondió con estado ${response.status}`);
        }
        return response.json();
      })
      .finally(() => {
        pendingCartRequest = null;
      });

    return pendingCartRequest;
  }

  async function mirrorRegionToCart(regionCode) {
    if (currentItemCount === 0) return;

    const region = REGION_BY_CODE.get(regionCode);
    const mirrorKey = `${currentCartToken || 'sin-token'}:${regionCode || 'sin-region'}`;

    if (mirrorKey === lastMirroredKey) return;
    lastMirroredKey = mirrorKey;

    const attributeValue = region
      ? `${region.code} | ${region.name} | mínimo ${region.minimum} CLP`
      : null;

    try {
      const response = await fetch(getCartUpdateUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            [PRIVATE_CART_ATTRIBUTE]: attributeValue,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Shopify respondió con estado ${response.status}`);
      }
    } catch (error) {
      lastMirroredKey = '';
      console.warn('No fue posible guardar la región en el carrito.', error);
    }
  }

  async function refreshAllInstances() {
    try {
      const cart = await fetchCart();

      currentCartToken = cart.token || '';
      currentItemCount = Number.parseInt(cart.item_count || 0, 10) || 0;

      const totals = calculateEligibleTotals(cart);

      instances.forEach((instance) => {
        instance.updateCartData(
          totals.eligible,
          cart.item_count,
          cart.token,
          totals.excluded
        );
      });

      const selectedRegion = readStoredRegion();
      if (selectedRegion) mirrorRegionToCart(selectedRegion);
    } catch (error) {
      console.warn('No fue posible actualizar la barra de envío gratis.', error);
    }
  }

  function scheduleRefresh(delay = 120) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshAllInstances, delay);
  }

  class LmFreeShippingProgress extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.regionSelect = this.querySelector('[data-region-select]');
      this.promptElement = this.querySelector('[data-prompt]');
      this.statusElement = this.querySelector('[data-status]');
      this.messageElement = this.querySelector('[data-message]');
      this.progressElement = this.querySelector('[data-progress]');
      this.progressFill = this.querySelector('[data-progress-fill]');
      this.currentTotalElement = this.querySelector('[data-current-total]');
      this.minimumElement = this.querySelector('[data-minimum]');

      if (!this.regionSelect || !this.progressElement) return;

      this.currentTotalCents = Number.parseInt(this.dataset.initialTotal || '0', 10) || 0;
      this.excludedTotalCents = Number.parseInt(this.dataset.excludedTotal || '0', 10) || 0;
      this.currentItemCount = Number.parseInt(this.dataset.initialCount || '0', 10) || 0;

      this.populateRegions();
      this.regionSelect.value = readStoredRegion();

      this._onSelectChange = this.onSelectChange.bind(this);
      this._onSharedRegionChange = this.onSharedRegionChange.bind(this);

      this.regionSelect.addEventListener('change', this._onSelectChange);
      document.addEventListener(REGION_CHANGE_EVENT, this._onSharedRegionChange);

      instances.add(this);
      this.render();
      scheduleRefresh(0);
    }

    disconnectedCallback() {
      if (this.regionSelect && this._onSelectChange) {
        this.regionSelect.removeEventListener('change', this._onSelectChange);
      }

      if (this._onSharedRegionChange) {
        document.removeEventListener(REGION_CHANGE_EVENT, this._onSharedRegionChange);
      }

      instances.delete(this);
    }

    populateRegions() {
      if (this.regionSelect.dataset.populated === 'true') return;

      REGIONS.forEach((region) => {
        const option = document.createElement('option');
        option.value = region.code;
        option.textContent = region.name;
        this.regionSelect.appendChild(option);
      });

      this.regionSelect.dataset.populated = 'true';
    }

    onSelectChange(event) {
      /*
       * El selector puede estar dentro de <cart-drawer-items>.
       * El tema escucha todos los eventos "change" de ese componente
       * como si vinieran de un campo de cantidad. Evitamos que este cambio
       * de región llegue al validador de cantidades del carrito.
       */
      event?.stopPropagation();

      const regionCode = this.regionSelect.value;

      storeRegion(regionCode);
      lastMirroredKey = '';
      this.render();

      document.dispatchEvent(
        new CustomEvent(REGION_CHANGE_EVENT, {
          detail: { regionCode, source: this },
        })
      );

      mirrorRegionToCart(regionCode);
    }

    onSharedRegionChange(event) {
      if (event.detail?.source === this) return;

      const regionCode = REGION_BY_CODE.has(event.detail?.regionCode)
        ? event.detail.regionCode
        : '';

      this.regionSelect.value = regionCode;
      this.render();
    }

    updateCartData(totalPrice, itemCount, token, excludedTotal = 0) {
      this.currentTotalCents = Number.parseInt(totalPrice || 0, 10) || 0;
      this.excludedTotalCents = Number.parseInt(excludedTotal || 0, 10) || 0;
      this.currentItemCount = Number.parseInt(itemCount || 0, 10) || 0;

      if (token) currentCartToken = token;
      currentItemCount = this.currentItemCount;

      this.render();
    }

    render() {
      if (this.currentItemCount === 0) {
        this.hidden = true;
        return;
      }

      this.hidden = false;

      const selectedRegion = REGION_BY_CODE.get(this.regionSelect.value);

      if (!selectedRegion) {
        this.classList.remove('is-complete');
        this.promptElement.hidden = false;
        this.statusElement.hidden = true;
        this.progressFill.style.width = '0%';
        this.progressElement.setAttribute('aria-valuenow', '0');
        this.progressElement.removeAttribute('aria-valuetext');
        return;
      }

      this.promptElement.hidden = true;
      this.statusElement.hidden = false;

      const currentTotalClp = Math.max(0, Math.round(this.currentTotalCents / 100));
      const remaining = Math.max(0, selectedRegion.minimum - currentTotalClp);
      const progressPercentage = Math.min(
        100,
        Math.max(0, (currentTotalClp / selectedRegion.minimum) * 100)
      );

      this.progressFill.style.width = `${progressPercentage.toFixed(2)}%`;
      this.progressElement.setAttribute('aria-valuenow', String(Math.round(progressPercentage)));

      this.currentTotalElement.textContent = `Subtotal que acumula: ${formatMoney(currentTotalClp)}`;
      this.minimumElement.textContent = `Meta: ${formatMoney(selectedRegion.minimum)}`;

      if (remaining === 0) {
        this.classList.add('is-complete');
        this.messageElement.innerHTML =
          `<strong>¡Listo!</strong> Tu carrito alcanzó el monto estimado ` +
          `para envío gratis a ${selectedRegion.name}.`;
        this.progressElement.setAttribute(
          'aria-valuetext',
          'Monto estimado para envío gratis alcanzado'
        );
      } else {
        this.classList.remove('is-complete');
        this.messageElement.innerHTML =
          `Te faltan <strong>${formatMoney(remaining)}</strong> ` +
          `para el envío gratis a ${selectedRegion.name}.`;
        this.progressElement.setAttribute(
          'aria-valuetext',
          `Faltan ${formatMoney(remaining)} para el envío gratis`
        );
      }
    }
  }

  customElements.define(ELEMENT_NAME, LmFreeShippingProgress);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleRefresh(80));
  }

  ['cart:updated', 'cart:change', 'cart:update', 'cart:refresh'].forEach((eventName) => {
    document.addEventListener(eventName, () => scheduleRefresh(100));
  });

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.matches('input[name^="updates"], .quantity__input, [data-quantity-input]')
      ) {
        scheduleRefresh(500);
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          'cart-remove-button, .quantity__button, [data-cart-remove], [name="update"], [href*="/cart/change"]'
        )
      ) {
        scheduleRefresh(550);
      }
    },
    true
  );

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;

    const regionCode = REGION_BY_CODE.has(event.newValue) ? event.newValue : '';
    document.dispatchEvent(
      new CustomEvent(REGION_CHANGE_EVENT, {
        detail: { regionCode, source: null },
      })
    );
  });
})();
